import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { JobRequestWorkflows } from 'src/pages/job-request-workflows/job-request-workflows';

import { Util } from 'src/shared/util/util';

@Component({
  selector: 'pro-search-agent',
  templateUrl: 'pro-search-agent.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['pro-search-agent.scss'],
  standalone: false
})
export class ProSearchAgentPage implements OnInit, OnDestroy {

  backPage = 'more';
  loaded = false;
  errorMessage: string;
  form: FormGroup;
  propertyFilter: number | null = null;
  propertyFiltersList = [
    { viewValue: 'All Properties', value: 'All Properties' }
  ];
  headers = ['Properties', 'Service', 'Actions', 'last-column'];
  rows: any[] = [];
  currentRows: any[] = [];
  addressResponse: any;
  panelClosedSubscription: Subscription;
  isRightSideContent = false;

  constructor(
    private client: Client,
    private fb: FormBuilder,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService,
    private util: Util
  ) {
    this.form = this.fb.group({
      propertyFilter: ['All Properties']
    });
  }

  async ngOnInit() {
    try {
      this.backPage = localStorage.getItem('proSearchAgentBackPage') || 'more';
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      await this.loadPage();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage, 10000);
    }
  }

  ngOnDestroy() {
    if (this.panelClosedSubscription) {
      this.panelClosedSubscription.unsubscribe();
    }
  }

  async loadPage() {
    this.loaded = false;
    this.addressResponse = await this.client.getMoreDetailAddresses();
    this.buildPropertyFiltersList();
    await this.buildRows();
    this.loaded = true;
  }

  buildPropertyFiltersList() {
    this.propertyFiltersList = [
      { viewValue: 'All Properties', value: 'All Properties' }
    ];
    if (this.addressResponse && this.addressResponse.length > 0) {
      this.addressResponse.forEach(address => {
        const propertyName = address.address_name || address.address1 || `Property ${address.id}`;
        this.propertyFiltersList.push({
          viewValue: propertyName,
          value: address.id
        });
      });
    }
  }

  async buildRows() {
    const rows = [];
    const addressIds = this.propertyFilter ? [this.propertyFilter] : this.addressResponse.map((address) => address.id);
    const addressIdsString = addressIds.join(',');
    const params = {
      page: 1,
      per_page: 10000,
      filters: {
        address_ids: addressIdsString,
      }
    };
    const queryString = this.buildQueryString(params);
    const addressHomekeeperPriorities = await this.client.getAddressHomekeeperPriorities(queryString);

    const groupedPriorities = addressHomekeeperPriorities.reduce((groups, priority) => {
      const key = `${priority.address_id}_${priority.service_type?.id || 'no-service'}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(priority);
      return groups;
    }, {} as Record<string, any[]>);

    const groupedAndSortedPriorities = Object.values(groupedPriorities).map((group: any[]) =>
      group.sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
    );

    groupedAndSortedPriorities.forEach((priorityGroup) => {
      const address = this.addressResponse.find((addr) => addr.id === priorityGroup[0].address_id);
      if (!address) {
        return;
      }
      rows.push({
        'Properties': {
          value: address.address_name || address.address1,
          color: address.color,
          addressId: address.id,
          action: null,
        },
        'Service': {
          value: priorityGroup[0].service_type.name,
          icon: priorityGroup[0].service_type.icon_url,
          action: null,
        },
        'Actions': {
          value: `${new TranslationPipe().transform('Contact')} ${priorityGroup.map((priority) => priority?.object?.first_name || `${new TranslationPipe().transform('new pros')}`).join(', ')}`,
          action: null,
          icon: 'assets/img/contact-people.svg',
          truncate: true
        },
        'last-column': {
          value: '',
          action: null,
        },
        'type': {
          type: 'address-homekeeper-priority',
          address_ids: [address.id],
        }
      });
    });

    this.rows = rows;
    this.currentRows = rows;
  }

  buildQueryString(params: any): string {
    const queryParts: string[] = [];
    if (params.page) {
      queryParts.push(`page=${params.page}`);
    }
    if (params.per_page) {
      queryParts.push(`per_page=${params.per_page}`);
    }
    if (params.filters) {
      if (params.filters.address_ids) {
        queryParts.push(`filters[address_ids]=${params.filters.address_ids}`);
      }
    }
    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  async changePropertyFilter(value) {
    if (value === 'All Properties') {
      this.propertyFilter = null;
    } else {
      this.propertyFilter = value;
    }
    this.loaded = false;
    this.rows = [];
    await this.buildRows();
    this.loaded = true;
  }

  goBack() {
    this.navCtrl.navigateForward(this.backPage);
  }

  goToWorkflowFromCard(row: any) {
    const event = { element: row };
    this.goToWorkflow(event);
  }

  async goToWorkflow(event) {
    const addressId = event.element.type.address_ids[0];
    localStorage.setItem('addressId', addressId.toString());
    const params = { addressId };

    if (this.windowService.isDesktopRes) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo('job-request-workflows', params, JobRequestWorkflows);
      } else {
        await this.storage.save('dialog-right-side-open', true);
        await this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(JobRequestWorkflows);
      }
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward('job-request-workflows', params);
    }
  }

}
